/**
 * Investment Detail Page
 *
 * Shows full investment details with status lifecycle transitions.
 * Data from GET /api/v1/impact-core/investments/{id}
 * Status transitions via PATCH /api/v1/impact-core/investments/{id}/status?new_status=xxx
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Briefcase,
  DollarSign,
  Calendar,
  Percent,
  Building,
  FileText,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Investment, InvestmentStatus, Fund, Investee } from '@/types';

const STATUS_VARIANTS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'muted'> = {
  pipeline: 'default',
  due_diligence: 'info',
  approved: 'warning',
  disbursed: 'info',
  active: 'success',
  matured: 'muted',
  exited: 'muted',
};

const STATUS_TRANSITIONS: Record<string, InvestmentStatus[]> = {
  pipeline: ['due_diligence'],
  due_diligence: ['approved', 'pipeline'],
  approved: ['disbursed', 'due_diligence'],
  disbursed: ['active'],
  active: ['matured', 'exited'],
  matured: [],
  exited: [],
};

export function InvestmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pendingTransition, setPendingTransition] = useState<InvestmentStatus | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const investmentQuery = useQuery<Investment>({
    queryKey: ['ic', 'investment', id],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/investments/${id}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!id,
  });

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const investeesQuery = useQuery<Investee[]>({
    queryKey: ['ic', 'investees'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investees/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (newStatus: InvestmentStatus) => {
      const res = await apiClient.patch(
        `/api/v1/impact-core/investments/${id}/status`,
        null,
        { params: { new_status: newStatus } }
      );
      return res.data;
    },
    onSuccess: () => {
      setPendingTransition(null);
      void queryClient.invalidateQueries({ queryKey: ['ic', 'investment', id] });
      void queryClient.invalidateQueries({ queryKey: ['ic', 'investments'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.delete(`/api/v1/impact-core/investments/${id}`);
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'investments'] });
      navigate('/investments');
    },
  });

  const inv = investmentQuery.data;

  if (investmentQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 animate-pulse rounded bg-slate-700/50" />
        <div className="h-32 animate-pulse rounded-lg bg-slate-700/30" />
        <div className="h-64 animate-pulse rounded-lg bg-slate-700/30" />
      </div>
    );
  }

  if (investmentQuery.isError || !inv) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-400">Failed to load investment details.</p>
          <button
            onClick={() => navigate('/investments')}
            className="mt-2 text-xs text-ic-accent hover:underline"
          >
            Back to Investments
          </button>
        </div>
      </div>
    );
  }

  const fundName = inv.fund_name ?? fundsQuery.data?.find((f) => f.id === inv.fund_id)?.name ?? '--';
  const investeeName = inv.investee_name ?? investeesQuery.data?.find((i) => i.id === inv.investee_id)?.name ?? 'Investee (removed)';
  const transitions = STATUS_TRANSITIONS[inv.status] ?? [];

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <div>
        <button
          onClick={() => navigate('/investments')}
          className="mb-3 flex items-center gap-1.5 text-xs text-ic-text-tertiary hover:text-ic-text-secondary transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Investments
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-semibold text-ic-text-primary">
                {inv.investment_code ?? `INV-${inv.id}`}
              </h1>
              <StatusBadge
                label={inv.status.replace('_', ' ')}
                variant={STATUS_VARIANTS[inv.status] ?? 'default'}
                size="md"
              />
            </div>
            <p className="mt-1 text-xs text-ic-text-tertiary">
              {investeeName} &middot; {fundName}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/investments')}
              className="flex items-center gap-1.5 rounded-md border border-ic-border px-3 py-2 text-xs font-medium text-ic-text-secondary hover:border-ic-accent/50 hover:text-ic-accent transition-colors"
            >
              <Edit2 className="h-3.5 w-3.5" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 rounded-md border border-red-500/30 px-3 py-2 text-xs font-medium text-red-400 hover:border-red-500/60 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Status Lifecycle Section */}
      {(transitions.length > 0 || pendingTransition) && (
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
                Status Lifecycle
              </span>
              <StatusBadge
                label={inv.status.replace('_', ' ')}
                variant={STATUS_VARIANTS[inv.status] ?? 'default'}
                size="md"
              />
            </div>
            {transitions.length > 0 && !pendingTransition && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-ic-text-tertiary mr-1">Transition to:</span>
                {transitions.map((next) => (
                  <button
                    key={next}
                    onClick={() => setPendingTransition(next)}
                    className="rounded-md border border-ic-border px-2.5 py-1 text-[10px] font-medium text-ic-text-secondary hover:border-ic-accent/50 hover:text-ic-accent transition-colors capitalize"
                  >
                    {next.replace('_', ' ')}
                  </button>
                ))}
              </div>
            )}
            {pendingTransition && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-ic-text-secondary">
                  Change status from <strong className="text-ic-text-primary capitalize">{inv.status.replace('_', ' ')}</strong> to{' '}
                  <strong className="text-ic-accent capitalize">{pendingTransition.replace('_', ' ')}</strong>?
                </span>
                <button
                  onClick={() => statusMutation.mutate(pendingTransition)}
                  disabled={statusMutation.isPending}
                  className="rounded-md bg-ic-accent px-3 py-1 text-[10px] font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
                >
                  {statusMutation.isPending ? 'Updating...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setPendingTransition(null)}
                  className="rounded-md border border-ic-border px-3 py-1 text-[10px] font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Grid */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-ic-border bg-ic-card p-4 space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">Investment Details</h3>
          <div className="grid grid-cols-2 gap-3">
            <MetaField icon={<Building className="h-3.5 w-3.5" />} label="Fund" value={fundName} />
            <MetaField icon={<Briefcase className="h-3.5 w-3.5" />} label="Investee" value={investeeName} />
            <MetaField icon={<FileText className="h-3.5 w-3.5" />} label="Instrument" value={inv.instrument_type ?? '--'} />
            <MetaField icon={<DollarSign className="h-3.5 w-3.5" />} label="Amount" value={`${inv.currency} ${inv.amount.toLocaleString()}`} />
            <MetaField icon={<Percent className="h-3.5 w-3.5" />} label="Sector" value={inv.sector ?? '--'} />
            <MetaField icon={<Calendar className="h-3.5 w-3.5" />} label="Investment Date" value={inv.date ? format(parseISO(inv.date), 'dd MMM yyyy') : '--'} />
            <MetaField icon={<Calendar className="h-3.5 w-3.5" />} label="Maturity Date" value={inv.maturity_date ? format(parseISO(inv.maturity_date), 'dd MMM yyyy') : '--'} />
            <MetaField icon={<DollarSign className="h-3.5 w-3.5" />} label="Currency" value={inv.currency} />
          </div>
        </div>

        {/* Timestamps & Notes */}
        <div className="space-y-4">
          <div className="rounded-lg border border-ic-border bg-ic-card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary mb-3">Timestamps</h3>
            <div className="space-y-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">Created</span>
                <p className="text-xs text-ic-text-secondary">
                  {inv.created_at ? format(parseISO(inv.created_at), 'dd MMMM yyyy, HH:mm') : '--'}
                </p>
              </div>
              {inv.updated_at && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">Last Updated</span>
                  <p className="text-xs text-ic-text-secondary">
                    {format(parseISO(inv.updated_at), 'dd MMMM yyyy, HH:mm')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-ic-border bg-ic-card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary mb-3">Notes</h3>
            <p className="text-xs text-ic-text-tertiary">No notes attached to this investment.</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)] p-6">
            <h3 className="text-sm font-semibold text-ic-text-primary mb-2">Delete Investment</h3>
            <p className="text-xs text-ic-text-secondary mb-4">
              Are you sure you want to delete <strong>{inv.investment_code ?? `INV-${inv.id}`}</strong>? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="rounded-md bg-red-500 px-4 py-2 text-xs font-medium text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-Components ────────────────────────────────────────────

function MetaField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-ic-text-tertiary">{icon}</span>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">{label}</p>
        <p className="text-xs font-medium text-ic-text-primary">{value}</p>
      </div>
    </div>
  );
}
